import { deriveCongressPresentation } from "../../../../../api/src/shared/domain/congress-lifecycle";
import type { SupportedLanguage } from "../../../interfaces";
import {
  hasReadArticle,
  isRecentArticle,
} from "../../article/utils/articleReads";
import { resolveCongressNames } from "../../congress/congressName";
import type { RoomEntry } from "../hooks/useGetRoomSectionsQuery";

/**
 * The Resources chip row after "All": one chip per entry of the room's order,
 * IN THAT ORDER — the server's order (domain/room-order.ts), never re-sorted
 * here. A congress space's look (congress, or archived as a section past its
 * grace window) is decided live from `now`; it changes the chip's icon, never
 * its place.
 */
export function resourceChips(
  entries: readonly RoomEntry[],
  context: { now: Date; contentLang: string; activeLang: SupportedLanguage }
) {
  return entries.map((entry) => {
    switch (entry.kind) {
      case "congress":
        return {
          kind: "congress" as const,
          // The `?section=` token mobile filters on; `spaceId` is the space.
          id: `congress:${entry.id}`,
          spaceId: entry.id,
          label: resolveCongressNames(
            entry.translations,
            entry.fallbackName,
            context.contentLang,
            entry.city,
            entry.country
          ).shortName,
          icon:
            deriveCongressPresentation(entry, context.now) === "congress"
              ? ("congress" as const)
              : ("school" as const),
          newContent: false,
        };
      case "section": {
        const activeTranslation = entry.translations[context.activeLang];
        const fallback = Object.values(entry.translations).find(
          (t) => t !== null
        );
        return {
          kind: "section" as const,
          id: entry.id,
          label:
            activeTranslation !== null
              ? activeTranslation.name
              : fallback === undefined
                ? ""
                : fallback.name,
          icon: "school" as const,
          newContent: entry.items.some(
            (item) =>
              item.type === "article" &&
              isRecentArticle(item.article.meta.creationDate) &&
              !hasReadArticle(item.article.id)
          ),
        };
      }
      default:
        throw new Error(entry satisfies never);
    }
  });
}
